import React from "react";
import { Document, Page, StyleSheet } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import type { MiContenidoMetricsExportData } from "../miContenidoExportData";
import {
  reportWebComparisonPeriodLabel,
  type ReportWebComparisonGranularity,
} from "../metricsReportWeb/reportWebTheme";
import { showProgramPageChannelNames } from "../programPages/sortProgramPagesForExport";
import { rankedProgramCardPages } from "../rankCardsPageLayout";
import { resolveWeeklyMetricsFloorYmd } from "../weeklyMetricsFloor";
import {
  formatFooterDate,
  formatLongDate,
  formatPeriodLabel,
  formatShortPeriodLabel,
  formatWeekPeriodNaturalLabel,
  PLATFORM_BREAKDOWN_VIEWS_EXPLAINER,
} from "./formatHelpers";
import {
  METRICS_PDF_COVER_PADDING_BOTTOM,
  METRICS_PDF_COVER_PADDING_TOP,
  METRICS_PDF_DECK_PROGRAM_CONTENT_PADDING_TOP,
  METRICS_PDF_PAGE_PADDING_HORIZONTAL,
  METRICS_PDF_PAGE_SIZE,
} from "./constants";
import { MetricsPdfCoverPage } from "./MetricsPdfCoverPage";
import { MetricsPdfDeckPage } from "./MetricsPdfDeckPage";
import { PeriodSummaryPdfSection } from "./PeriodSummaryPdfSection";
import { ProgramPagePdfSection } from "./ProgramPagePdfSection";
import { sanitizePdfDisplayText } from "./sanitizePdfDisplayText";
import { syncPdfTheme, usePdfTheme } from "./PdfThemeContext";
import { PDF_FONT_FAMILY, type PdfColorMode } from "./pdfTheme";
import { registerMetricsPdfFonts } from "./registerMetricsPdfFonts";

registerMetricsPdfFonts();

type Props = {
  data: MiContenidoMetricsExportData;
  companyId?: string | null;
  comparisonGranularity?: ReportWebComparisonGranularity;
  colorMode?: PdfColorMode;
  showPeriodComparisons?: boolean;
};

function MetricsPdfDocumentPages({
  data,
  companyId,
  comparisonGranularity,
  showPeriodComparisons = true,
}: Omit<Props, "colorMode">): React.ReactElement {
  const { t } = useTranslation("metricsPdf");
  const theme = usePdfTheme();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        coverPage: {
          flexDirection: "column",
          backgroundColor: theme.pageBg,
          paddingTop: METRICS_PDF_COVER_PADDING_TOP,
          paddingBottom: METRICS_PDF_COVER_PADDING_BOTTOM,
          paddingHorizontal: METRICS_PDF_PAGE_PADDING_HORIZONTAL,
          fontFamily: PDF_FONT_FAMILY,
          fontSize: 13,
          color: theme.textBody,
        },
      }),
    [theme],
  );

  const { branding, period } = data;
  const streamingFormat = data.streamingFormat ?? "live";
  const generated = formatFooterDate();
  const generatedLong = formatLongDate();
  const periodLabel = formatPeriodLabel(period.startDate, period.endDate);
  const periodEyebrow =
    comparisonGranularity === "week"
      ? formatWeekPeriodNaturalLabel(period.startDate, period.endDate)
      : formatShortPeriodLabel(period.startDate, period.endDate);
  const comparisonPeriodLabel = reportWebComparisonPeriodLabel(
    comparisonGranularity,
  );
  const weeklyMetricsFloorYmd = resolveWeeklyMetricsFloorYmd({
    fromExport: period.weeklyMetricsFloorYmd,
    companyId,
  });
  const showProgramChannelNames = showProgramPageChannelNames(
    data.programPages,
  );

  const rankCardPages = rankedProgramCardPages(data.programRanking?.videos);
  const periodSummaryTitle =
    comparisonGranularity === "week"
      ? t("periodSummaryWeek")
      : t("periodSummaryPeriod");

  const deckProps = { branding, generated: generated };

  return (
    <>
      <Page size={METRICS_PDF_PAGE_SIZE} style={styles.coverPage} wrap={false}>
        <MetricsPdfCoverPage
          branding={branding}
          periodLabel={periodLabel}
          generated={generatedLong}
          {...(data.audienceDemoPlaceholder
            ? { audienceDemoPlaceholder: true }
            : {})}
        />
      </Page>

      {rankCardPages.map((pageRows, pageIndex) => (
        <MetricsPdfDeckPage
          key={`period-summary-${pageIndex}`}
          {...deckProps}
          headerTitle={periodSummaryTitle}
          headerPeriodLabel={periodEyebrow}
        >
          <PeriodSummaryPdfSection
            rows={pageRows}
            rankingPeriod={data.programRanking?.rankingPeriod ?? null}
            podscopeActiveProgramCount={
              data.programRanking?.podscopeActiveProgramCount ?? null
            }
            streamingFormat={streamingFormat}
          />
        </MetricsPdfDeckPage>
      ))}

      {data.programPages?.map((page) => (
        <MetricsPdfDeckPage
          key={page.programId}
          {...deckProps}
          headerTitle={sanitizePdfDisplayText(page.name)}
          headerPeriodLabel={periodEyebrow}
          contentPaddingTop={METRICS_PDF_DECK_PROGRAM_CONTENT_PADDING_TOP}
          footerNote={PLATFORM_BREAKDOWN_VIEWS_EXPLAINER}
          {...(page.imageUrl ? { headerImageUrl: page.imageUrl } : {})}
          {...(showProgramChannelNames && page.channelName
            ? { headerSubtitle: sanitizePdfDisplayText(page.channelName) }
            : {})}
        >
          <ProgramPagePdfSection
            page={page}
            reportStartDate={period.startDate}
            reportEndDate={period.endDate}
            weeklyMetricsFloorYmd={weeklyMetricsFloorYmd}
            streamingFormat={streamingFormat}
            comparisonPeriodLabel={comparisonPeriodLabel}
            showPeriodComparisons={showPeriodComparisons}
          />
        </MetricsPdfDeckPage>
      ))}
    </>
  );
}

export function MiContenidoMetricsPdfDocument({
  data,
  companyId,
  comparisonGranularity,
  colorMode = "light",
  showPeriodComparisons = true,
}: Props): React.ReactElement {
  // Must run before children render — react-pdf often skips React context.
  syncPdfTheme(colorMode);

  return (
    <Document>
      <MetricsPdfDocumentPages
        data={data}
        companyId={companyId}
        comparisonGranularity={comparisonGranularity}
        showPeriodComparisons={showPeriodComparisons}
      />
    </Document>
  );
}
